/**
 * Purpose: View-only modal overlay showing the classroom desk grid.
 * Owns: Dark overlay backdrop, chalkboard header, teacher's desk, DeskGrid rendering,
 *       and close button. No turn-flow logic — purely informational.
 * Notes: Lets the player peek at their current classroom position anytime during
 *        gameplay, not just after a correct answer. Reads state from GameContext.
 *        Tap outside or close button to dismiss. All colors use theme tokens (CS-22).
 */

import { useGame } from '../../context/GameContext';
import { DeskGrid } from '../classroom/DeskGrid';
import type { Category } from '../../types/question';

// Subject icons matching the rest of the app (CS-20: no magic strings).
const SUBJECT_ICONS: Record<Category | 'Final Exam', string> = {
  Math: '\u{1F9EE}',
  Science: '\u{1F52C}',
  History: '\u{1F4DC}',
  English: '\u{1F4D6}',
  Art: '\u{1F3A8}',
  Geography: '\u{1F30D}',
  Music: '\u{1F3B5}',
  Sports: '\u{1F3C8}',
  'Final Exam': '\u{1F393}',
};

interface ClassroomPreviewOverlayProps {
  /** Called when the overlay should close */
  onClose: () => void;
}

export function ClassroomPreviewOverlay({ onClose }: ClassroomPreviewOverlayProps) {
  const { state } = useGame();
  const activePlayer = state.players[state.currentPlayerIndex];

  if (!activePlayer) return null;

  const periodIndex = activePlayer.currentPeriod - 1;
  const periodData = activePlayer.schedule[periodIndex];
  if (!periodData) return null;

  const subjectName = periodData.subject;
  const subjectIcon = SUBJECT_ICONS[subjectName as keyof typeof SUBJECT_ICONS] ?? '';

  return (
    /* Dark overlay — tap backdrop to dismiss */
    <div
      style={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Classroom preview"
    >
      {/* Content panel — stop propagation so tapping inside doesn't dismiss */}
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header with subject and close button */}
        <div style={styles.header}>
          <span className="chalk-text-bright" style={styles.headerText}>
            {subjectIcon} Period {activePlayer.currentPeriod}: {subjectName}
          </span>
          <button
            style={styles.closeButton}
            onClick={onClose}
            aria-label="Close classroom preview"
          >
            &times;
          </button>
        </div>

        {/* Teacher's desk */}
        <div style={styles.teacherDeskRow}>
          <div style={styles.teacherDesk}>
            <span className="chalk-text" style={styles.teacherLabel}>Teacher</span>
          </div>
        </div>

        {/* Desk grid — the actual classroom view */}
        <div style={styles.gridArea}>
          <DeskGrid
            classSize={periodData.classSize}
            players={state.players}
            periodIndex={periodIndex}
            activePlayerId={activePlayer.id}
            animatingSeat={null}
          />
        </div>
      </div>
    </div>
  );
}

// _______________________________________________
//              STYLES (CS-22: theme tokens only)
// _______________________________________________

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: '1rem',
  },
  panel: {
    backgroundColor: 'var(--bg-chalkboard)',
    border: '3px solid var(--accent-wood)',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '400px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 1rem',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    borderBottom: '3px solid var(--accent-wood)',
  },
  headerText: {
    fontSize: '1.1rem',
    letterSpacing: '0.04em',
  },
  closeButton: {
    fontSize: '1.5rem',
    color: 'var(--text-light)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem 0.5rem',
    lineHeight: 1,
    opacity: 0.7,
    minHeight: '48px',
    minWidth: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  teacherDeskRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '0.5rem 1rem 0.25rem',
  },
  teacherDesk: {
    width: '100px',
    height: '28px',
    backgroundColor: 'var(--accent-wood)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  teacherLabel: {
    fontSize: '0.6rem',
    opacity: 0.7,
    letterSpacing: '0.08em',
  },
  gridArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.75rem',
    overflowY: 'auto',
  },
};
